import 'react-toggle/style.css'
import './assets/main.css'
// eslint-disable-next-line import/no-unresolved
import '@aws-amplify/ui-react/styles.css'

import { Authenticator } from '@aws-amplify/ui-react'
import * as Sentry from '@sentry/react'
import TagManager from '@sooro-io/react-gtm-module'
import { QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider } from 'antd'
import { AuthProvider } from 'auth/authContext'
import { Amplify } from 'aws-amplify'
import { globalQueryClient } from 'queries/queryClient'
import React from 'react'
import { CookiesProvider } from 'react-cookie'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { ThemeProvider } from 'styled-components'

import amplifyconfig from './amplifyconfiguration.json'
import App from './App'
import ProductFruitsIntegration from './ProductFruitsIntegration'
import * as serviceWorker from './serviceWorker'
import store from './store'
import antdTheme from './themes/antdTheme'
import lightTheme from './themes/lightTheme'
import IntegrationsContextProvider from './utils/IntegrationsContext'
import ZentakeContextProvider from './utils/ZentakeContext'

TagManager.initialize({
  gtmId: 'GTM-562MBJP',
  nonce: process.env.REACT_APP_GTM_NONCE,
})

Amplify.configure({
  ...amplifyconfig,
})

if (process.env.REACT_APP_SENTRY_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV,
    sampleRate: 1.0, // ✅ Capture 100% of error events
    tracesSampleRate: 0.05, // ✅ 5% of frontend transactions sampled for performance
    profilesSampleRate: 0.0, // ✅ No CPU profiling (saves cost & bandwidth)
    replaysSessionSampleRate: 0, // ✅ Do not record every session
    replaysOnErrorSampleRate: 0.1, // ✅ Record replay only on error (10%)
    attachStacktrace: true, // ✅ Include stacktrace for all captured errors
    sendDefaultPii: true, // ✅ Include IP, user info (if set via setUser)
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({
        failedRequestTargets: [
          // Capture failed requests to your API
          /api\.app\.zentake\.io/,
          /file-server-alt\.zentake\.io/,
        ],
      }),
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllText: false,
        networkDetailAllowUrls: [
          /^https?:\/\/api\.app\.zentake\.io/,
          /^https?:\/\/file-server-alt\.zentake\.io/,
          /^https?:\/\/cognito-idp\.us-west-2\.amazonaws\.com/,
        ],
        networkCaptureBodies: true,
        networkRequestHeaders: ['Authorization', 'Content-Type', 'Accept'],
        networkResponseHeaders: ['Content-Type', 'Content-Length', 'Server'],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.extraErrorDataIntegration(),
    ],
    beforeSend(event) {
      if (event.exception) {
        event.extra = {
          ...event.extra,
          isOnline: navigator.onLine,
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          viewportSize: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Removed memory, connection, and cookiesEnabled to reduce data
        }
      }
      return event
    },
  })
}

ReactDOM.render(
  <Authenticator.Provider>
    <CookiesProvider>
      <AuthProvider>
        <QueryClientProvider client={globalQueryClient}>
          <ZentakeContextProvider>
            <Provider store={store}>
              <ProductFruitsIntegration />
              <IntegrationsContextProvider>
                <ThemeProvider theme={lightTheme}>
                  <ConfigProvider theme={antdTheme}>
                    <App />
                  </ConfigProvider>
                </ThemeProvider>
              </IntegrationsContextProvider>
            </Provider>
          </ZentakeContextProvider>
        </QueryClientProvider>
      </AuthProvider>
    </CookiesProvider>
  </Authenticator.Provider>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
